import React, { useEffect, useState } from "react";
import { useAuthFetch } from "../lib/authFetch";
import { useCorpora, corpusLabel, CorpusListing, CorpusPermissions } from "../lib/useCorpora";

const CORPUS_API = process.env.REACT_APP_CORPUS_API_URL ?? "http://localhost:3002";
const INDEXING_API = process.env.REACT_APP_API_BASE_URL ?? "http://localhost:3001";

interface CorpusMember {
  clerkUserId: string;
  isOwner: boolean;
  permissions: CorpusPermissions;
  subscribedAt: string | null;
}

function PermChips({ p, owned }: { p: CorpusPermissions; owned: boolean }) {
  if (owned) {
    return <span className="px-2 py-0.5 rounded text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">owner</span>;
  }
  const chips: { key: keyof CorpusPermissions; label: string }[] = [
    { key: "admin", label: "admin" },
    { key: "read", label: "read" },
    { key: "write", label: "write" },
    { key: "delete", label: "delete" },
  ];
  const active = chips.filter((c) => p[c.key]);
  if (active.length === 0) return <span className="text-xs text-kfaint">—</span>;
  return (
    <>
      {active.map((c) => (
        <span key={c.key} className="px-2 py-0.5 rounded text-xs bg-ksky/10 text-inklight border border-ksky/30">
          {c.label}
        </span>
      ))}
    </>
  );
}

/** Owner/admin modal: view members, grant permissions, revoke access. */
function MembersModal({ corpus, onClose }: { corpus: CorpusListing; onClose: () => void }) {
  const authFetch = useAuthFetch();
  const [members, setMembers] = useState<CorpusMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [grantUserId, setGrantUserId] = useState("");
  const [grant, setGrant] = useState<CorpusPermissions>({ admin: false, read: true, write: false, delete: false });

  const load = React.useCallback(() => {
    authFetch(`${CORPUS_API}/api/corpora/${encodeURIComponent(corpus.corpusId)}/members`)
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
        setMembers((body as { members: CorpusMember[] }).members);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load members"));
  }, [authFetch, corpus.corpusId]);

  useEffect(() => { load(); }, [load]);

  const setPermissions = async (userId: string, perms: CorpusPermissions) => {
    setBusy(true);
    setError(null);
    try {
      const res = await authFetch(
        `${CORPUS_API}/api/corpora/${encodeURIComponent(corpus.corpusId)}/members/${encodeURIComponent(userId)}`,
        { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(perms) },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
      }
      setGrantUserId("");
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to set permissions");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (userId: string) => {
    setBusy(true);
    setError(null);
    try {
      await authFetch(
        `${CORPUS_API}/api/corpora/${encodeURIComponent(corpus.corpusId)}/members/${encodeURIComponent(userId)}`,
        { method: "DELETE" },
      );
      load();
    } finally {
      setBusy(false);
    }
  };

  const PermCheck = ({ field, label, value, onChange }: {
    field: keyof CorpusPermissions; label: string; value: boolean; onChange: (v: boolean) => void;
  }) => (
    <label className="flex items-center gap-1.5 text-xs text-inklight cursor-pointer" key={field}>
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 rounded border-krule text-ink focus:ring-kamber/70" />
      {label}
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-kterm/70 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-krule shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-krule">
          <div>
            <h3 className="text-base font-medium text-ink">Access — {corpusLabel(corpus)}</h3>
            <p className="text-xs font-mono text-kfaint mt-0.5">{corpus.corpusId}</p>
          </div>
          <button onClick={onClose} className="text-kfaint hover:text-ink text-xl leading-none">×</button>
        </div>

        <div className="px-6 py-4 space-y-5">
          {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

          {/* Members */}
          {members === null ? (
            <p className="text-sm text-kslate">Loading members…</p>
          ) : (
            <div className="border border-krule rounded-lg divide-y divide-krule/60">
              {members.map((m) => (
                <div key={m.clerkUserId} className="flex items-center gap-3 px-4 py-2.5">
                  <span className="flex-1 min-w-0 text-xs font-mono text-inklight truncate" title={m.clerkUserId}>
                    {m.clerkUserId}
                  </span>
                  <span className="flex gap-1.5 shrink-0">
                    <PermChips p={m.permissions} owned={m.isOwner} />
                  </span>
                  {!m.isOwner && (
                    <button onClick={() => revoke(m.clerkUserId)} disabled={busy}
                      className="shrink-0 text-xs text-kslate hover:text-red-700 disabled:opacity-50">
                      Revoke
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Grant */}
          <div className="border border-krule rounded-lg p-4">
            <p className="text-sm font-medium text-ink mb-1">Grant access</p>
            <p className="text-xs text-kslate mb-3">
              Enter a Clerk user id. Write allows adding new documents (not changing existing ones);
              admin allows granting access to others.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="text" value={grantUserId} onChange={(e) => setGrantUserId(e.target.value)}
                placeholder="user_…"
                className="flex-1 min-w-[220px] text-sm font-mono border border-krule rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-kamber/70"
              />
              <PermCheck field="read" label="read" value={grant.read} onChange={(v) => setGrant((g) => ({ ...g, read: v }))} />
              <PermCheck field="write" label="write" value={grant.write} onChange={(v) => setGrant((g) => ({ ...g, write: v }))} />
              <PermCheck field="delete" label="delete" value={grant.delete} onChange={(v) => setGrant((g) => ({ ...g, delete: v }))} />
              <PermCheck field="admin" label="admin" value={grant.admin} onChange={(v) => setGrant((g) => ({ ...g, admin: v }))} />
              <button
                onClick={() => setPermissions(grantUserId.trim(), grant)}
                disabled={busy || !grantUserId.trim()}
                className="bg-ink hover:bg-inklight disabled:bg-ink/30 text-white text-xs font-medium rounded-lg px-4 py-2 transition-colors"
              >
                Grant
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Corpora page — lists every corpus available to the user with contents and
 * permissions, lets them create corpora, subscribe to public ones, and (for
 * owners/admins) manage member access.
 */
const CorporaPage: React.FC = () => {
  const { corpora, loading, error, refresh } = useCorpora();
  const authFetch = useAuthFetch();

  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newVisibility, setNewVisibility] = useState<"private" | "public">("private");
  const [newSubscribable, setNewSubscribable] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [membersFor, setMembersFor] = useState<CorpusListing | null>(null);
  const [reindexFor, setReindexFor] = useState<CorpusListing | null>(null);
  const [reindexing, setReindexing] = useState(false);
  const [reindexResult, setReindexResult] = useState<string | null>(null);
  const [reindexType, setReindexType] = useState<string>("");
  const [reindexTypes, setReindexTypes] = useState<Array<{ type_name: string; document_count: number }>>([]);

  // Load the corpus's type vocabulary when the re-index modal opens (#108).
  useEffect(() => {
    if (!reindexFor) { setReindexTypes([]); setReindexType(""); return; }
    let cancelled = false;
    authFetch(`${INDEXING_API}/query/corpus-types?corpusId=${encodeURIComponent(reindexFor.corpusId)}`)
      .then((res) => res.json())
      .then((body) => {
        if (!cancelled) setReindexTypes(((body as { types?: Array<{ type_name: string; document_count: number }> }).types ?? []).filter((t) => t.document_count > 0));
      })
      .catch(() => { if (!cancelled) setReindexTypes([]); });
    return () => { cancelled = true; };
  }, [reindexFor, authFetch]);

  const handleReindex = async (c: CorpusListing) => {
    setReindexing(true);
    setReindexResult(null);
    try {
      const res = await authFetch(`${CORPUS_API}/api/corpora/${encodeURIComponent(c.corpusId)}/reindex`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reindexType ? { documentType: reindexType } : {}),
      });
      const body = (await res.json().catch(() => ({}))) as { queued?: number; skipped?: number; error?: string };
      if (!res.ok) {
        setReindexResult(`Failed: ${body.error ?? `HTTP ${res.status}`}`);
        return;
      }
      setReindexResult(
        `Queued ${body.queued ?? 0} document(s) for re-indexing` +
        ((body.skipped ?? 0) > 0 ? `; ${body.skipped} skipped (never parsed)` : "") + ".",
      );
    } catch (err) {
      setReindexResult(`Failed: ${err instanceof Error ? err.message : "network error"}`);
    } finally {
      setReindexing(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    try {
      const res = await authFetch(`${CORPUS_API}/api/corpora`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newName.trim(),
          description: newDescription.trim() || undefined,
          visibility: newVisibility,
          subscribable: newVisibility === "public" && newSubscribable,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCreateError((body as { error?: string }).error ?? `HTTP ${res.status}`);
        return;
      }
      setNewName("");
      setNewDescription("");
      setNewVisibility("private");
      setNewSubscribable(false);
      refresh();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Network error");
    } finally {
      setCreating(false);
    }
  };

  const handleSubscription = async (c: CorpusListing) => {
    setBusyId(c.corpusId);
    try {
      await authFetch(`${CORPUS_API}/api/corpora/${encodeURIComponent(c.corpusId)}/subscribe`, {
        method: c.subscribed ? "DELETE" : "POST",
      });
      refresh();
    } finally {
      setBusyId(null);
    }
  };

  const fmtDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString() : "—";

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 h-[78px] border-b border-krule bg-white shrink-0">
        <div>
          <h2 className="text-xl font-light tracking-tight text-ink">Corpora</h2>
          <p className="text-xs font-mono text-kslate mt-0.5">Document collections you own or subscribe to</p>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
        <div className="max-w-4xl space-y-6">
          {/* Create form */}
          <form onSubmit={handleCreate} className="bg-white border border-krule rounded-xl p-5">
            <h3 className="text-base font-medium text-ink mb-1">Create a corpus</h3>
            <p className="text-xs text-kslate mb-4">
              You own the corpora you create. Public + open-for-subscription corpora are listed to
              every user (subscribing grants read only); private corpora are invite-only — you or an
              admin grant access.
            </p>
            <div className="space-y-3">
              <div className="flex gap-3">
                <input
                  type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
                  placeholder="Name — e.g. Research Notes"
                  className="flex-1 text-sm border border-krule rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-kamber/70"
                />
                <select
                  value={newVisibility}
                  onChange={(e) => setNewVisibility(e.target.value as "private" | "public")}
                  className="text-sm border border-krule rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-kamber/70"
                >
                  <option value="private">Private</option>
                  <option value="public">Public</option>
                </select>
              </div>
              <textarea
                value={newDescription} onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Description — what does this corpus contain?"
                rows={2}
                className="w-full text-sm border border-krule rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-kamber/70"
              />
              <div className="flex items-center justify-between">
                <label className={`flex items-center gap-2 text-sm cursor-pointer ${newVisibility === "public" ? "text-inklight" : "text-kfaint"}`}>
                  <input
                    type="checkbox"
                    checked={newVisibility === "public" && newSubscribable}
                    disabled={newVisibility !== "public"}
                    onChange={(e) => setNewSubscribable(e.target.checked)}
                    className="h-4 w-4 rounded border-krule text-ink focus:ring-kamber/70"
                  />
                  Open for subscription (users can self-subscribe, read-only)
                </label>
                <button
                  type="submit"
                  disabled={creating || !newName.trim()}
                  className="bg-ink hover:bg-inklight disabled:bg-ink/30 text-white text-sm font-medium rounded-lg px-5 py-2 transition-colors"
                >
                  {creating ? "Creating…" : "Create"}
                </button>
              </div>
            </div>
            {createError && <p className="mt-2 text-xs text-red-600">{createError}</p>}
          </form>

          {/* Listing */}
          {loading && <p className="text-sm text-kslate">Loading corpora…</p>}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          )}

          {!loading && !error && (
            <div className="bg-white border border-krule rounded-xl overflow-hidden">
              <div className="grid grid-cols-[1.6fr_0.9fr_1fr_auto] gap-3 px-5 py-2.5 bg-paper/50 border-b border-krule text-xs font-medium text-kslate uppercase tracking-wide">
                <span>Corpus</span>
                <span>Contents</span>
                <span>Access</span>
                <span className="w-56 text-right">Actions</span>
              </div>
              <div className="divide-y divide-krule/60">
                {corpora.map((c) => (
                  <div key={c.corpusId} className="grid grid-cols-[1.6fr_0.9fr_1fr_auto] gap-3 px-5 py-3 items-center">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium text-ink truncate">{corpusLabel(c)}</span>
                        {c.isDefault && (
                          <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] bg-kamber/15 text-ink border border-kamber/40">default</span>
                        )}
                        <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] bg-paper text-kslate border border-krule">
                          {c.visibility}
                        </span>
                        {c.subscribable && (
                          <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] bg-paper text-kslate border border-krule">open</span>
                        )}
                      </div>
                      {c.description && (
                        <p className="text-xs text-kslate mt-0.5 line-clamp-2" title={c.description}>{c.description}</p>
                      )}
                      <p className="text-[10px] font-mono text-kfaint truncate mt-0.5">{c.corpusId}</p>
                    </div>
                    <div className="text-xs text-kslate">
                      <div><span className="font-medium text-ink">{c.documentCount.toLocaleString()}</span> documents</div>
                      <div className="text-kfaint">last added {fmtDate(c.lastAddedAt)}</div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <PermChips p={c.permissions} owned={c.owned} />
                    </div>
                    <div className="w-56 flex justify-end gap-2">
                      {(c.owned || c.permissions.admin) && c.documentCount > 0 && (
                        <button
                          onClick={() => { setReindexResult(null); setReindexType(""); setReindexFor(c); }}
                          className="text-xs font-medium rounded-lg px-3 py-1.5 border border-krule text-ink hover:bg-paper transition-colors"
                        >
                          Re-index…
                        </button>
                      )}
                      {(c.owned || c.permissions.admin) && c.kind === "shared" && (
                        <button
                          onClick={() => setMembersFor(c)}
                          className="text-xs font-medium rounded-lg px-3 py-1.5 border border-krule text-ink hover:bg-paper transition-colors"
                        >
                          Access…
                        </button>
                      )}
                      {!c.owned && (c.subscribed || (c.visibility === "public" && c.subscribable)) && (
                        <button
                          onClick={() => handleSubscription(c)}
                          disabled={busyId === c.corpusId}
                          className={`text-xs font-medium rounded-lg px-3 py-1.5 border transition-colors disabled:opacity-50 ${
                            c.subscribed
                              ? "border-krule text-kslate hover:text-red-700 hover:border-red-200 hover:bg-red-50"
                              : "border-krule text-ink hover:bg-paper"
                          }`}
                        >
                          {busyId === c.corpusId ? "…" : c.subscribed ? "Unsubscribe" : "Subscribe"}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {corpora.length === 0 && (
                  <p className="px-5 py-4 text-sm text-kslate">No corpora found.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {membersFor && <MembersModal corpus={membersFor} onClose={() => { setMembersFor(null); refresh(); }} />}

      {reindexFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => !reindexing && setReindexFor(null)}>
          <div
            className="w-full max-w-md mx-4 bg-white rounded-xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-krule">
              <h3 className="text-base font-medium text-ink">
                Re-index “{corpusLabel(reindexFor)}”?
              </h3>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm text-inklight">
              <p>
                This re-runs <span className="font-medium">classification, AI analysis, and search indexing</span>{" "}
                (documents are not re-parsed) for:
              </p>
              <select
                value={reindexType}
                onChange={(e) => setReindexType(e.target.value)}
                className="w-full text-sm border border-krule rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-kamber/70"
              >
                <option value="">All {reindexFor.documentCount.toLocaleString()} documents</option>
                {reindexTypes.map((t) => (
                  <option key={t.type_name} value={t.type_name}>
                    Only "{t.type_name}" ({t.document_count.toLocaleString()} documents)
                  </option>
                ))}
              </select>
              <ul className="list-disc pl-5 space-y-1 text-xs text-kslate">
                <li>Incurs LLM cost proportional to corpus size — unchanged summaries are reused from cache where possible.</li>
                <li>Under emergent classification, document types may change and new types may be created.</li>
                <li>Runs in the background; documents update as the indexing worker processes the queue.</li>
              </ul>
              {reindexResult && (
                <p className={`text-xs font-medium ${reindexResult.startsWith("Failed") ? "text-red-600" : "text-emerald-700"}`}>
                  {reindexResult}
                </p>
              )}
            </div>
            <div className="px-5 py-3 bg-paper/50 border-t border-krule flex justify-end gap-2">
              <button
                onClick={() => setReindexFor(null)}
                disabled={reindexing}
                className="text-xs font-medium rounded-lg px-4 py-2 border border-krule text-kslate hover:bg-paper transition-colors disabled:opacity-50"
              >
                {reindexResult && !reindexResult.startsWith("Failed") ? "Close" : "Cancel"}
              </button>
              {!(reindexResult && !reindexResult.startsWith("Failed")) && (
                <button
                  onClick={() => handleReindex(reindexFor)}
                  disabled={reindexing}
                  className="text-xs font-medium rounded-lg px-4 py-2 bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
                >
                  {reindexing
                    ? "Queuing…"
                    : `Re-index ${(reindexType
                        ? reindexTypes.find((t) => t.type_name === reindexType)?.document_count ?? 0
                        : reindexFor.documentCount
                      ).toLocaleString()} documents`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorporaPage;
